/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { useRef, useState, useMemo, useEffect, forwardRef } from "react";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import SlickGrid, { typedColumns, type ColumnDefinition, type GridRowBase, type SlickGrid1Ref } from ".";
import ModalStateDialogComponent from "../modal/ModalWithState";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import { useTranslation } from "@i18n";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import Text from "../Text/Text";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import {
  Formatters,
  SortDirection,
  type Column,
  type SlickDataView,
  type SlickGrid as SlickGridType,
  type SlickgridReactInstance,
} from "slickgrid-react";
import { InputAdornment } from "@mui/material";
import IconComponent from "@CubeComponents/icons";

const SPECIAL_COLUMN_IDS = new Set(["_checkbox_selector", "_move"]);

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

export interface GridColumn extends GridRowBase {
  id: string;
  /**
   * The row's label. A group row keyed by `columnGroupId` alone carries no `columnGroup` to label it
   * with, and slickgrid's `treeFormatter` early-returns an empty cell on an `undefined` value
   * (node_modules/@slickgrid-universal/common/dist/esm/formatters/treeFormatter.js). The absence is
   * therefore carried as `undefined` rather than defaulted to `""`, which would render the group
   * toggle against a blank label instead.
   */
  name?: string;
  field?: string;
  hidden?: boolean;
  index?: number;
  hasChild?: boolean;
  parentId?: string | null;
  treeLevel?: number;
  isGroupColumn?: boolean;
}

/**
 * A host-grid column as the chooser reads it: only the keys it touches, plus the grouping metadata
 * `SlickGrid1` columns carry. Slickgrid's own `Column` widens `id`/`name` beyond `string`, so the
 * two boundaries where columns arrive from the grid are read through `toChooserColumn`.
 */
interface ChooserColumn {
  id: string;
  /** Absent when the host column carries no label; see the `name` note on `GridColumn`. */
  name?: string;
  excludeFromColumnPicker?: boolean;
  columnGroupId?: string;
  columnGroup?: string;
}

/**
 * Reads a host-grid column as a `ChooserColumn`: slickgrid widens `id` to `number | string` and
 * `name` to a DOM node, neither of which the chooser can list or search. The rest of the column is
 * carried through, because the chooser's rows are built by spreading it. `columnGroupId` is this
 * app's own column-definition property, not one of slickgrid's.
 *
 * A non-string `name` is carried as ABSENT rather than as `""` - the two render differently (see the
 * `name` note on `GridColumn`). The `id` is the one value that is converted: the chooser keys its
 * rows, its selection set and the `onSave` payload by string. Both boundaries the columns arrive
 * through (`getAllColumnDefinitions()` and `getColumns()`) are read the same way, so the two sides
 * always match. Every column definition in the app supplies a string id, but `ColumnDefinition.id`
 * admits `string | number`: a numeric one would reach `onSave` as its decimal string, which
 * `ToolBar`'s `allColDefs.find((c) => c.id === colId)` would then not match. Widening the chooser
 * (and `onSave`) to `string | number` is the fix for that; it changes this component's exported
 * prop type.
 */
function toChooserColumn(col: Column & { columnGroupId?: string }): ChooserColumn {
  return { ...col, id: String(col.id), name: typeof col.name === "string" ? col.name : undefined };
}

/** The host grid whose columns are being chosen; supplied on this component's ref. */
type ChooserGridRef = React.RefObject<SlickGrid1Ref<GridRowBase> | null>;

/**
 * `SlickDataView.getItem` is typed as always returning a row, but it indexes the rendered rows and
 * returns `undefined` past the last one - an index the RowMoveManager legitimately hands over when a
 * row is dropped at the end of the list. The library's own `getItem` guards for it the same way.
 */
function getRowAt(dataView: SlickDataView<GridColumn>, index: number): GridColumn | undefined {
  return dataView.getItem(index);
}

/**
 * Renders the tree column: the group toggle plus the column label.
 *
 * `Formatters.tree` cannot be handed to `ColumnDefinition["formatter"]` directly - that signature
 * declares `columnDef: ColumnDefinition<T, K>`, and `ColumnDefinition` is not assignable to
 * slickgrid's own `Column` (its `ExcelExportColumnOptions.valueParserCallback` returns `unknown`
 * where the library's `GetDataValueCallback` returns `string | number | Date | ExcelColumnMetadata`).
 * The column the library formatter wants is therefore read back off the grid at the library's own
 * type: `getColumns()[cell]` is the very object slickgrid would have passed, since `appendCellHtml`
 * calls the formatter with `this.columns[cell]` and `getColumns()` returns `this.columns`
 * (node_modules/@slickgrid-universal/common/dist/esm/core/slickGrid.js).
 */
function treeCellFormatter(
  row: number,
  cell: number,
  value: string | undefined,
  _columnDef: ColumnDefinition<GridColumn, "name">,
  dataContext: GridColumn,
  grid?: SlickGridType,
): unknown {
  const column = grid?.getColumns()[cell];
  if (!grid || !column) return "";
  return Formatters.tree(row, cell, value, column, dataContext, grid);
}

/** The payload slickgrid's RowMoveManager hands its move callbacks (it also passes `grid`). */
interface RowMoveArgs {
  rows: number[];
  insertBefore: number;
}

/** A dragged parent row together with the child rows that travel with it. */
interface RowBlock {
  start: number;
  items: GridColumn[];
}

interface GridColumnChooserProps {
  /** Receives the ids of the columns to keep visible, in their new order. */
  onSave?: (columnIds: string[]) => void;
  onCancel?: () => void;
}

const GridColumnChooser = forwardRef(
  ({ onSave, onCancel }: GridColumnChooserProps, ref: React.ForwardedRef<SlickGrid1Ref<GridRowBase>>) => {
    const { t } = useTranslation();
    // `forwardRef` also admits a callback ref, which exposes no `.current` for the chooser to read
    // the host grid from; narrow to the ref-object case instead of asserting one onto the other.
    const dataGrid: ChooserGridRef | null = typeof ref === "function" ? null : ref;
    const gridRef = useRef<SlickGrid1Ref<GridColumn> | null>(null);

    const selectedIdsRef = useRef<string[]>([]);
    const columnsRef = useRef<GridColumn[]>([]);

    const specialColumns = useRef<ChooserColumn[]>([]);

    const [searchValue, setSearchValue] = useState("");
    const [showOnlySelected, setShowOnlySelected] = useState(false);
    // Slickgrid freezes `rowMoveManager.onBeforeMoveRows/onMoveRows` (and the cell formatters) at
    // grid-creation time, so those callbacks keep the FIRST render's `searchValue` /
    // `showOnlySelected` forever. Mirror both into refs so the frozen callbacks read live values.
    const searchValueRef = useRef("");
    const showOnlySelectedRef = useRef(false);
    // Only read as an effect dependency - the grid instance itself is never used here.
    const [gridRendered, setGridRendered] = useState<object>({});

    const [initialDataset, setInitialDataset] = useState<GridColumn[]>([]);

    useEffect(() => {
      if (!dataGrid?.current) return;

      const gridService = dataGrid.current.gridService;
      const slickGrid = dataGrid.current.slickGrid;
      const allColumns = gridService.getAllColumnDefinitions().map(toChooserColumn);

      const visibleIds = slickGrid.getColumns().map((col: Column) => String(col.id));
      const visibleSet = new Set(visibleIds);
      const visibleIndexMap = new Map(visibleIds.map((id: string, i: number): [string, number] => [id, i]));

      const getSortIndex = (id: string): number =>
        visibleIndexMap.has(id) ? visibleIndexMap.get(id)! : Number.MAX_SAFE_INTEGER;

      const specialCols: ChooserColumn[] = [];
      const groupedCols: Record<string, { groupName?: string; minIndex: number; cols: ChooserColumn[] }> = {};
      const orphanCols: { minIndex: number; col: ChooserColumn }[] = [];

      for (const col of allColumns) {
        if (SPECIAL_COLUMN_IDS.has(col.id) || col.excludeFromColumnPicker) {
          specialCols.push(col);
          continue;
        }

        const sortIndex = getSortIndex(col.id);

        const groupKey = col.columnGroupId || col.columnGroup;
        if (groupKey) {
          if (!groupedCols[groupKey]) {
            // `columnGroup` is what supplies the header label; a column grouped only by
            // `columnGroupId` has none, and the group row then renders an empty cell (see the
            // `name` note on `GridColumn`).
            groupedCols[groupKey] = {
              groupName: col.columnGroup,
              minIndex: Number.MAX_SAFE_INTEGER,
              cols: [],
            };
          }
          const group = groupedCols[groupKey]!;
          group.cols.push(col);
          if (sortIndex < group.minIndex) group.minIndex = sortIndex;
        } else {
          orphanCols.push({ minIndex: sortIndex, col });
        }
      }

      specialColumns.current = specialCols;

      const sortedGroups = Object.entries(groupedCols).map(([groupKey, group]) => ({
        type: "group" as const,
        id: groupKey,
        groupName: group.groupName,
        minIndex: group.minIndex,
        cols: group.cols.sort((a, b) => getSortIndex(a.id) - getSortIndex(b.id)),
      }));

      const sortedOrphans = orphanCols.map((o) => ({
        type: "orphan" as const,
        id: o.col.id,
        minIndex: o.minIndex,
        col: o.col,
      }));

      const mergedList = [...sortedGroups, ...sortedOrphans].sort((a, b) => a.minIndex - b.minIndex);

      let finalFlatList: GridColumn[] = [];

      for (const item of mergedList) {
        if (item.type === "orphan") {
          finalFlatList.push({
            ...item.col,
            parentId: null,
            hasChild: false,
            __hasChildren: false,
            treeLevel: 0,
          });
        } else {
          finalFlatList.push({
            id: item.id,
            name: item.groupName,
            field: item.id,
            isGroupColumn: true,
            parentId: null,
            hasChild: true,
            __collapsed: false,
            __hasChildren: true,
            hidden: false,
            treeLevel: 0,
          });

          item.cols.forEach((col: ChooserColumn) => {
            finalFlatList.push({
              ...col,
              parentId: item.id,
              hasChild: false,
              __hasChildren: false,
              treeLevel: 1,
            });
          });
        }
      }

      finalFlatList = finalFlatList.map((col: GridColumn, index: number) => ({ ...col, index }));
      columnsRef.current = finalFlatList;
      selectedIdsRef.current = visibleIds;

      const dataset = finalFlatList.map((col: GridColumn) => ({
        ...col,
        hidden: col.isGroupColumn ? false : !visibleSet.has(col.id),
      }));

      setInitialDataset(dataset);
    }, [gridRendered, dataGrid]);

    useEffect(() => {
      if (initialDataset.length > 0 && gridRef.current?.dataView) {
        gridRef.current.dataView.setItems(initialDataset);
        gridRef.current.slickGrid.invalidate();
        gridRef.current.slickGrid.render();
      }
    }, [initialDataset]);

    const updateGridData = (
      searchVal: string = searchValueRef.current,
      showSelected: boolean = showOnlySelectedRef.current,
    ) => {
      const dv = gridRef.current?.dataView;
      if (!dv) return;

      const allCols = columnsRef.current;
      const selectedSet = new Set(selectedIdsRef.current);

      let displayIds = new Set<string>();

      if (showSelected) {
        selectedSet.forEach((id: string) => displayIds.add(id));

        selectedSet.forEach((id: string) => {
          const col = allCols.find((c: GridColumn) => c.id === id);
          if (col?.parentId) displayIds.add(col.parentId);
        });
      } else {
        allCols.forEach((c: GridColumn) => displayIds.add(c.id));
      }

      if (searchVal) {
        const regex = new RegExp(escapeRegExp(searchVal), "i");

        const baseVisible = allCols.filter((c: GridColumn) => displayIds.has(c.id));

        // An unlabelled group row has no name to match; it is still pulled in below as the parent of
        // any child that matches.
        const matched = baseVisible.filter((c: GridColumn) => c.name !== undefined && regex.test(c.name));

        const searchIds = new Set<string>();

        matched.forEach((col: GridColumn) => {
          searchIds.add(col.id);
          if (col.parentId) searchIds.add(col.parentId);
        });

        displayIds = searchIds;
      }

      const finalData = allCols
        .filter((c: GridColumn) => displayIds.has(c.id))
        .map((c: GridColumn) => ({
          ...c,
          hidden: !selectedSet.has(c.id),

          parentId: c.parentId ?? null,
          hasChild: c.hasChild ?? false,
          treeLevel: c.treeLevel,
          isGroupColumn: c.isGroupColumn,
        }));

      dv.setItems(finalData);
      gridRef.current?.slickGrid.invalidate();
      gridRef.current?.slickGrid.render();
    };

    const onBeforeMoveRows = (e: Event, data: RowMoveArgs): boolean => {
      const dv = gridRef.current?.dataView;
      if (!dv) return false;

      const { rows, insertBefore } = data;

      for (const r of rows) {
        const item = getRowAt(dv, r);

        if (item?.parentId) {
          e.stopPropagation();
          return false;
        }

        const targetItem = getRowAt(dv, insertBefore);
        if (targetItem?.parentId) {
          e.stopPropagation();
          return false;
        }

        if (r === insertBefore || r === insertBefore - 1) {
          e.stopPropagation();
          return false;
        }
      }

      return true;
    };

    const onMoveRows = (_e: Event, args: RowMoveArgs) => {
      const { rows, insertBefore } = args;
      const dv = gridRef.current?.dataView;
      const slick = gridRef.current?.slickGrid;
      if (!dv) return;

      // CUBE-21119: the grid rows are the *displayed* list, which is a SUBSET whenever a search
      // term or "Show only Selected" is active. Reordering that subset and writing it back to
      // `columnsRef` deleted every filtered-out column from the chooser's master list for the
      // rest of the modal's life (they disappeared from the list and could never be re-checked,
      // and `handleSave` then dropped them from the grid). Resolve the drag back to column ids
      // and reorder the MASTER list instead.
      const movedIds: string[] = rows
        .slice()
        .sort((a: number, b: number) => a - b)
        .map((rowIndex: number) => getRowAt(dv, rowIndex))
        .filter((row): row is GridColumn => !!row && !row.parentId)
        .map((row: GridColumn) => row.id);
      if (!movedIds.length) return;

      // Anchor the drop on the id currently displayed at `insertBefore`; a drop past the last
      // displayed row appends to the end of the master list.
      const anchorId = getRowAt(dv, insertBefore)?.id ?? null;
      if (anchorId !== null && movedIds.includes(anchorId)) return;

      const master = [...columnsRef.current];
      const blocks: RowBlock[] = [];
      for (const id of movedIds) {
        const start = master.findIndex((c: GridColumn) => c.id === id);
        if (start === -1) continue;
        const items = [master[start]!];
        let pointer = start + 1;
        while (pointer < master.length && master[pointer]!.parentId === id) {
          items.push(master[pointer]!);
          pointer++;
        }
        blocks.push({ start, items });
      }

      const movedBlockItems = blocks.flatMap((b: RowBlock) => b.items);
      const movedSet = new Set(movedBlockItems.map((c: GridColumn) => c.id));
      const remaining = master.filter((c: GridColumn) => !movedSet.has(c.id));
      const anchorIndex = anchorId === null ? -1 : remaining.findIndex((c: GridColumn) => c.id === anchorId);
      remaining.splice(anchorIndex === -1 ? remaining.length : anchorIndex, 0, ...movedBlockItems);

      columnsRef.current = remaining.map((row: GridColumn, index: number) => ({
        ...row,
        index,
        parentId: row.parentId ?? null,
        hasChild: row.hasChild ?? false,
        treeLevel: row.treeLevel,
        isGroupColumn: row.isGroupColumn,
        hidden: !selectedIdsRef.current.includes(row.id),
      }));

      // Re-project the (possibly filtered) view from the freshly reordered master list.
      updateGridData();
      slick?.resetActiveCell();
    };

    const handleCheckChange = (rowData: GridColumn, evtOrChecked: boolean | React.ChangeEvent<HTMLInputElement>) => {
      const nextChecked = typeof evtOrChecked === "boolean" ? evtOrChecked : evtOrChecked.target.checked;

      let updated = new Set(selectedIdsRef.current);

      const isParent = rowData.hasChild === true;
      const isChild = rowData.parentId !== null;

      const allCols = columnsRef.current;

      if (isParent) {
        const children = allCols.filter((col: GridColumn) => col.parentId === rowData.id);

        if (nextChecked) {
          updated.add(rowData.id);
          children.forEach((child: GridColumn) => updated.add(child.id));
        } else {
          updated.delete(rowData.id);
          children.forEach((child: GridColumn) => updated.delete(child.id));
        }
      } else if (isChild) {
        if (nextChecked) updated.add(rowData.id);
        else updated.delete(rowData.id);
      } else {
        if (nextChecked) updated.add(rowData.id);
        else updated.delete(rowData.id);
      }

      const order = allCols.map((r: GridColumn) => r.id);
      const finalSelected = Array.from(updated).sort((a, b) => order.indexOf(a) - order.indexOf(b));

      selectedIdsRef.current = finalSelected;

      const dv = gridRef.current?.dataView;
      if (!dv) return;

      // CUBE-17624: when "Show only selected" is on, an unchecked row must
      // disappear from the list (it's no longer selected). Re-run the search +
      // showOnlySelected filter against the freshly-updated `selectedIdsRef`
      // so the predicate reads current state, not the prior dataView snapshot.
      if (showOnlySelectedRef.current) {
        updateGridData();
        return;
      }

      const items = dv.getItems().map((r: GridColumn) => ({
        ...r,
        hidden: !finalSelected.includes(r.id),

        parentId: r.parentId ?? null,
        hasChild: r.hasChild ?? false,
        treeLevel: r.treeLevel,
        isGroupColumn: r.isGroupColumn,
      }));

      dv.setItems(items);
      gridRef.current?.slickGrid.invalidate();
      gridRef.current?.slickGrid.render();
    };

    const checkBoxComponentTemplate = (
      _row: number,
      _cell: number,
      _value: unknown,
      _col: unknown,
      data: GridColumn,
    ) => {
      const container = document.createElement("div");
      const root = createRoot(container);

      let indeterminate = false;
      let checked = false;

      if (data.isGroupColumn) {
        const childCols = columnsRef.current.filter((col: GridColumn) => col.parentId === data.id);

        const selectedSet = new Set(selectedIdsRef.current);

        const allSelected = childCols.length > 0 && childCols.every((col: GridColumn) => selectedSet.has(col.id));

        const someSelected = !allSelected && childCols.some((col: GridColumn) => selectedSet.has(col.id));

        if (allSelected) checked = true;
        else if (someSelected) indeterminate = true;
      } else {
        checked = selectedIdsRef.current.includes(data.id);
      }

      root.render(
        <CheckBoxComponent
          indeterminate={indeterminate}
          checked={checked}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCheckChange(data, e)}
        />,
      );

      return container;
    };

    const columnDefinitions = useMemo(
      () =>
        typedColumns<GridColumn>([
          {
            id: "checkboxColumnChooser",
            field: "id",
            name: "",
            maxWidth: 50,
            formatter: checkBoxComponentTemplate,
          },
          {
            id: "label",
            name: t("Field"),
            field: "name",
            sortable: true,
            minWidth: 300,
            formatter: treeCellFormatter,
          },
          { id: "index", name: t("Index"), field: "index", sortable: true, hidden: true },
        ]),

      [],
    );

    const gridOptions = useMemo(
      () => ({
        enableCheckboxSelector: false,
        // Always on: slickgrid builds RowMoveManager (and its `_move` handle column) once, at
        // grid creation, so flipping this afterwards was a silent no-op. Reordering inside a
        // filtered view is handled correctly by `onMoveRows` now, and the drag handles are
        // wanted in the "Show only Selected" view too (CUBE-21114).
        enableRowMoveManager: true,
        enableFiltering: true,
        forceFitColumns: true,

        rowMoveManager: { onBeforeMoveRows, onMoveRows },
        enableTreeData: true,
        treeDataOptions: {
          columnId: "index",
          parentPropName: "parentId",
          levelPropName: "treeLevel",
          indentMarginLeft: 25,
          initiallyCollapsed: false,
          hasChildrenPropName: "hasChild",
          initialSort: {
            columnId: "index",
            direction: SortDirection.ASC,
          },
        },
      }),
      [],
    );

    const handleSave = () => {
      // `selectedIdsRef` is the only authority on what is checked. The extra `!col.hidden` test
      // read a snapshot that only `onMoveRows` ever refreshed, so a column checked AFTER a
      // drag-reorder was silently dropped from the save (CUBE-21119).
      onSave?.(
        columnsRef.current
          .filter((col: GridColumn) => !col.isGroupColumn && selectedIdsRef.current.includes(col.id))
          .map((col: GridColumn) => col.id),
      );
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchValue(newValue);
      searchValueRef.current = newValue;
      updateGridData(newValue, showOnlySelectedRef.current);
    };

    const toggleShowOnlySelected = () => {
      const newValue = !showOnlySelected;
      setShowOnlySelected(newValue);
      showOnlySelectedRef.current = newValue;
      updateGridData(searchValueRef.current, newValue);
    };

    /**
     * Auto-scroll the grid viewport while the user is dragging a row near the
     * top/bottom edge. Slickgrid's built-in RowMoveManager doesn't support
     * auto-scroll; without this, rows drop silently when the target is off-screen.
     *
     * CUBE-15135: SlickGrid's RowMoveManager only recomputes `insertBefore` on
     * native mousemove (it reads `pageY` then). Auto-scrolling the viewport
     * without firing a mousemove leaves the drop target stuck on whatever row
     * the user was hovering BEFORE the scroll, so the drop lands on the wrong
     * row (or is silently rejected). Re-dispatch the last MouseEvent to the
     * canvas after every scroll tick so the drag guide tracks the new viewport
     * content.
     */
    useEffect(() => {
      const container = dataGrid?.current?.slickGrid.getContainerNode();
      if (!container) return;

      const EDGE_PX = 40;
      const SCROLL_PX = 12;
      let rafId: number | null = null;
      let lastClientY = 0;
      let lastClientX = 0;

      const getViewport = (): HTMLElement | null =>
        container.querySelector(".slick-viewport-left") ||
        container.querySelector(".slick-viewport") ||
        container.querySelector(".slick-viewport-right");

      const dispatchSyntheticMove = (): void => {
        const canvas = container.querySelector(".grid-canvas") as HTMLElement | null;
        const target = canvas ?? container;
        target.dispatchEvent(
          new MouseEvent("mousemove", {
            bubbles: true,
            cancelable: true,
            clientX: lastClientX,
            clientY: lastClientY,
            view: window,
          }),
        );
      };

      const tick = () => {
        rafId = null;
        const isDragging = document.querySelector(".slick-reorder-proxy") != null;
        if (!isDragging) return;
        const viewport = getViewport();
        if (!viewport) return;
        const rect = viewport.getBoundingClientRect();
        let delta = 0;
        if (lastClientY < rect.top + EDGE_PX) delta = -SCROLL_PX;
        else if (lastClientY > rect.bottom - EDGE_PX) delta = SCROLL_PX;
        if (delta !== 0) {
          const before = viewport.scrollTop;
          viewport.scrollTop += delta;
          if (viewport.scrollTop !== before) dispatchSyntheticMove();
          rafId = requestAnimationFrame(tick);
        }
      };

      const updatePointer = (clientX: number, clientY: number): void => {
        const isDragging = document.querySelector(".slick-reorder-proxy") != null;
        if (!isDragging) return;
        lastClientX = clientX;
        lastClientY = clientY;
        if (rafId == null) rafId = requestAnimationFrame(tick);
      };

      const onMouseMove = (e: MouseEvent) => updatePointer(e.clientX, e.clientY);
      const onDragOver = (e: DragEvent) => updatePointer(e.clientX, e.clientY);
      const onTouchMove = (e: TouchEvent) => {
        const t = e.touches[0];
        if (t) updatePointer(t.clientX, t.clientY);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("dragover", onDragOver);
      document.addEventListener("touchmove", onTouchMove, { passive: true });
      return () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("dragover", onDragOver);
        document.removeEventListener("touchmove", onTouchMove);
        if (rafId != null) cancelAnimationFrame(rafId);
      };
    }, [gridRendered, dataGrid]);

    return (
      <ModalStateDialogComponent
        footerContent={
          <div className="flex gap-4 pt-1">
            <ButtonComponent onClick={handleSave} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
              <Text>{t("Save")}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={onCancel || (() => {})} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </div>
        }
        footerBorderTop
        footerBorderColor="var(--stroke-primary)"
        headerColor="#E8F1FC"
        headerTitle={<Text className="font-bold">{t("Choose Columns")}</Text>}
        onCancel={onCancel || (() => {})}
        isVisible
        modelContent={
          <div className="h-full flex flex-col">
            <div className="flex flex-row justify-between mb-4 items-center">
              <div className="w-80">
                <TextFieldComponent
                  type="text"
                  value={searchValue}
                  onChange={handleSearch}
                  placeholder={t("Search Column")}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconComponent name="SearchIcon" width="1rem" height="1rem" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </div>
              <div className="flex flex-row gap-2 items-center">
                <ToggleSwitchComponent isChecked={showOnlySelected} onChange={toggleShowOnlySelected} />
                <Text className="text-sm">{t("Show only Selected")}</Text>
              </div>
            </div>
            <div className="flex-1">
              <SlickGrid
                ref={gridRef}
                surpassedDataset={initialDataset}
                hideNoDataIllustration
                disableHeader
                onReactGridCreated={(reactGrid: SlickgridReactInstance) => {
                  setGridRendered(reactGrid);
                }}
                hasChildMapping="hasChild"
                parentIdMapping="parentId"
                dropdownColumn={2}
                treeGrid
                columnDefinitions={columnDefinitions}
                gridOptions={gridOptions}
                showPageCount={false}
                disableCheckBoxSelection
                enableSelectAllCheckbox={false}
                disableChildDataFetch={true}
              />
            </div>
          </div>
        }
        width="80rem"
        height="45rem"
      />
    );
  },
);

GridColumnChooser.displayName = "GridColumnChooser";

export default GridColumnChooser;
